// utils/invoicePreview.ts
//
// Pure, stateless transform: takes a saved InvoiceRecord (backend shape)
// and returns a payload shaped exactly like the INVOICE_UPDATE message
// MobileInvoicePreview.tsx already knows how to render.
//
// This does NOT touch BillsView or POSView — it's new, additive logic,
// extracted from BillsView's openEdit()/liveEditInvoice so a headless
// print page (and eventually Bills itself) can call it directly without
// any React state.

import { InvoiceRecord, CanvasElement } from '../types';

// ─── Helpers (mirrors BillsView's inline logic) ────────────────────────────

function getInvoiceTaxId(bill: any): string {
  if (!bill) return '';
  const direct = bill.buyer_tax_id || bill.buyerTaxId || bill.clientGSTIN || bill.client_gstin || bill.gstin || bill.tax_id;
  if (direct && String(direct).trim()) return String(direct).trim();

  const tableEl = (bill.elements || []).find((el: any) => el.type === 'table');
  const tableTaxId = (tableEl as any)?.buyer_tax_id || (tableEl as any)?.buyerTaxId;
  if (tableTaxId && String(tableTaxId).trim()) return String(tableTaxId).trim();

  const dyn = bill.dynamicValues || bill.dynamic_values;
  const dynTaxId = dyn?.tax_id || dyn?.buyer_tax_id || dyn?.gstin;
  if (dynTaxId && String(dynTaxId).trim()) return String(dynTaxId).trim();

  for (const el of (bill.elements || [])) {
    if (el.type === 'text' && el.textContent) {
      const match = el.textContent.match(/(?:tax\s*id|gstin|npwp|vat|trn)\s*[:\-#]?\s*([A-Z0-9.\-]+)/i);
      if (match && match[1] && match[1].length >= 4) return match[1].trim();
    }
  }
  return '';
}

const isTaxOrContactLine = (l: string) =>
  l.includes('@') ||
  /[\d\+\-\(\)\s]{6,}/.test(l) ||
  /(gstin|tax\s*id|vat|trn|pan|تاكس|غستين|ضريب)/i.test(l);

function findBillToElement(elements: CanvasElement[]) {
  return elements.find((el: any) =>
    el.type === 'text' && el.textContent &&
    (
      el.textContent.toLowerCase().startsWith('bill to') ||
      el.textContent.toLowerCase().startsWith('client:') ||
      el.textContent.toLowerCase().startsWith('depositor') ||
      el.textContent.toLowerCase().startsWith('customer') ||
      el.textContent.toLowerCase().startsWith('patient:') ||
      el.textContent.toLowerCase().startsWith('sold to') ||
      el.textContent.toLowerCase().startsWith('owner:')
    )
  );
}

function extractClientAddress(bill: any, billToEl: any): string {
  const billToLines: string[] = billToEl?.textContent?.split('\n') || [];
  const extracted = billToLines.slice(2).filter((l: string) => !isTaxOrContactLine(l)).join(', ');
  return (bill as any).clientAddress || (bill as any).client_address || extracted || '';
}

function extractClientAddressAr(bill: any, billToEl: any): string {
  const billToArLines: string[] = billToEl?.arabicContent?.split('\n') || [];
  const rawArAddr =
    (bill as any).dynamicValuesAr?.client_address ||
    (bill as any).dynamic_values_ar?.client_address ||
    billToArLines.slice(2).filter((l: string) => !isTaxOrContactLine(l)).join('، ') ||
    '';
  return rawArAddr
    .split(/[،,]/)
    .filter((part: string) => !/(gstin|tax\s*id|vat|trn|pan|تاكس|غستين|ضريب)/i.test(part))
    .join('، ')
    .trim();
}

function extractClientPhone(bill: any, elements: CanvasElement[]): string {
  const billToEl2 = findBillToElement(elements);
  const billToLines2: string[] = billToEl2?.textContent?.split('\n') || [];
  const extractedPhone = billToLines2.find((l: string) => /[\d\+\-\(\)\s]{6,}/.test(l) && !l.includes('@')) || '';
  return (bill as any).clientPhone || extractedPhone || '';
}

// ─── Main export ────────────────────────────────────────────────────────────

export interface InvoicePreviewPayload {
  template: any;
  estimatedTableHeight?: number;
  invoiceDate: string;
  invoiceTime: string;
  lineItems: any[];
  taxLines: { label: string; rate: number }[];
  discountLines: { label: string; rate: number; _isAuto?: boolean }[];
  shippingCost: number;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientAddress: string;
  clientAddressAr?: string;
  clientNameAr?: string;
  buyerTaxId?: string;
  isB2B: boolean;
  notes: string;
  currency: string;
  isBilingual: boolean;
  bilingualMode?: boolean;
  translateTargetLang?: string;
  showAmountInWords?: boolean;
  amountInWords?: string;
  amountInWordsTranslated?: string;
  amount_in_words?: string;
  amount_in_words_ar?: string;
  translatedValues?: Record<string, any>;
  showInvoiceQR?: boolean;
  showTrackingQR: boolean;
  orderToken?: string;
  token?: string;
  trackingToken?: string;
  invoiceNumber: string;
  pageSize?: string;
  pageWidth?: number;
  isThermal?: boolean;
  autoQrSize?: number;
  autoQrLabelSize?: number;
  autoQrPosition?: string;
  taxBreakdown?: { name: string; rate: number; amount: number }[];
  taxBreakdownAr?: Record<string, string>;
  dynamicValues?: Record<string, string>;
  dynamicValuesAr?: Record<string, string>;
  dynamicFieldLabelsAr?: Record<string, string>;
  sellerCountry?: string;
  sellerRegion?: string;
  buyerRegion?: string;
  savedElements: CanvasElement[];
}

export function buildInvoicePreviewPayload(bill: InvoiceRecord): InvoicePreviewPayload {
  const elements = bill.elements || [];
  const tableEl: any = elements.find((el: any) => el.type === 'table');
  const billToEl = findBillToElement(elements);

  const clientName = (bill as any).clientName || (bill as any).client_name || '';
  const clientEmail = (bill as any).clientEmail || (bill as any).client_email || '';
  const clientPhone = extractClientPhone(bill, elements);
  const clientAddress = extractClientAddress(bill, billToEl);
  const buyerTaxId = getInvoiceTaxId(bill) || '';

  const wasTranslated =
    (bill as any).isBilingual === true ||
    (bill as any).is_bilingual === true ||
    (bill as any).bilingualMode === true ||
    (bill as any).bilingual_mode === true ||
    Boolean((bill as any).translateTargetLang || (bill as any).translate_target_lang);

  const clientAddressAr = wasTranslated ? extractClientAddressAr(bill, billToEl) : undefined;
  const clientNameAr = wasTranslated
    ? ((bill as any).dynamicValuesAr?.client_name ||
       (bill as any).dynamic_values_ar?.client_name ||
       (billToEl?.arabicContent?.split('\n') || [])[1] ||
       '')
    : undefined;

  // Read from bill.line_items first (where backend stores translations and custom taxes), fallback to tableEl.rows
  const rawItems = (bill.line_items && bill.line_items.length > 0)
    ? bill.line_items
    : (tableEl?.rows || []);

  const lineItems = rawItems.map((r: any) => {
    // If from tableEl.rows, data is nested in r.values; if from bill.line_items, data is top-level
    const v = r.values || r;
    const arabic =
      v.arabicName ||
      v.arabic_name ||
      v.translatedName ||
      v.translated_name ||
      v.arabicDescription ||
      v.arabic_description ||
      v.translatedDescription ||
      '';

    const customTaxName = v.taxName || v.tax_name;

    return {
      id: r.id || v.id || v.product_id,
      description: v.description || '',
      arabicName: arabic,
      translatedDescription: arabic,
      qty: parseFloat(String(v.qty)) || 1,
      rate: parseFloat(String(v.unit_price ?? v.rate ?? 0)) || 0,
      taxRate: parseFloat(String(v.taxRate ?? v.tax_rate ?? 0)) || 0,
      taxName: customTaxName,
      b2bTaxRate: v.b2bTaxRate ?? v.b2b_tax_rate,
      b2bTaxName: v.b2bTaxName ?? v.b2b_tax_name,
      taxInclusive: !!(v.taxInclusive ?? v.tax_inclusive),
    };
  });

  const rawTaxes = (tableEl as any)?.taxes || (bill as any).taxes || [];
  const rawDiscounts = (tableEl as any)?.discounts || (bill as any).discounts || [];

  const dyn = (bill as any).dynamic_values || (bill as any).dynamicValues || {};
  const dynAr = (bill as any).dynamic_values_ar || (bill as any).dynamicValuesAr || {};
  const taxLinesArList: string[] = (bill as any).tax_lines_ar || (bill as any).taxLinesAr || dynAr.tax_lines_ar || [];
  const discountLinesArList: string[] = (bill as any).discount_lines_ar || (bill as any).discountLinesAr || dynAr.discount_lines_ar || [];

  const dynamicTaxLabel = dynAr.label_tax || '';
  const dynamicDiscountLabel = dynAr.label_discount || '';

  const taxes = (rawTaxes.length > 0 ? rawTaxes : []).map(
    (t: any, idx: number) => {
      const translatedLabel =
        t.labelAr ||
        t.label_ar ||
        t.arabicLabel ||
        taxLinesArList[idx] ||
        dynamicTaxLabel;

      return {
        ...t,
        label: t.label || 'Tax',
        rate: Number(t.rate || 0),
        labelAr: translatedLabel,
        arabicLabel: translatedLabel,
      };
    }
  );

  const discounts = (rawDiscounts.length > 0 ? rawDiscounts : []).map(
    (d: any, idx: number) => {
      const translatedLabel =
        d.labelAr ||
        d.label_ar ||
        d.arabicLabel ||
        discountLinesArList[idx] ||
        dynamicDiscountLabel;

      return {
        ...d,
        label: d.label || 'Discount',
        rate: Number(d.rate || 0),
        labelAr: translatedLabel,
        arabicLabel: translatedLabel,
      };
    }
  );

  const isB2B = buyerTaxId.trim().length > 0;

  // Build custom tax breakdown so named taxes like "rihan (5%)" or "PPN (5%)" render on the PDF
  const customTaxItem = lineItems.find((i: any) => i.taxRate > 0 && i.taxName);
  const detectedTaxName = customTaxItem ? customTaxItem.taxName : null;

  // Resolve translated custom tax name from all possible saved locations
  const rawBreakdownAr =
    (bill as any).tax_breakdown_ar ||
    (bill as any).taxBreakdownAr ||
    dynAr.tax_breakdown_ar ||
    {};

  const fallbackTaxAr =
    dynAr.label_tax ||
    ((bill as any).tax_lines_ar && (bill as any).tax_lines_ar[0]) ||
    (dynAr.tax_lines_ar && dynAr.tax_lines_ar[0]) ||
    (taxes[0] && taxes[0].labelAr) ||
    '';

  const savedTaxBreakdownAr: Record<string, string> = {
    ...rawBreakdownAr,
  };

  if (detectedTaxName) {
    // Map the custom tax name (e.g. 'rihan') to its translation so the Arabic side is NEVER blank
    savedTaxBreakdownAr[detectedTaxName] =
      savedTaxBreakdownAr[detectedTaxName] ||
      fallbackTaxAr ||
      detectedTaxName;
  }

  let taxBreakdown: { name: string; rate: number; amount: number }[] | undefined = undefined;
  if (customTaxItem) {
    const totalTaxAmt = lineItems.reduce((acc: number, item: any) => {
      const lineTotal = item.qty * item.rate;
      const rate = item.taxRate || 0;
      if (rate <= 0) return acc;
      const tax = item.taxInclusive
        ? lineTotal - lineTotal / (1 + rate / 100)
        : lineTotal * (rate / 100);
      return acc + tax;
    }, 0);

    taxBreakdown = [{
      name: detectedTaxName,
      rate: customTaxItem.taxRate,
      amount: parseFloat(totalTaxAmt.toFixed(2)),
    }];
  }

  const translatedLang =
    (bill as any).translateTargetLang ||
    (bill as any).translate_target_lang ||
    (elements as any[]).find((el: any) => el.translatedLang)?.translatedLang ||
    'ar';

  // ⚡ 0. Detect Thermal roll vs A4 (Auto-detect from element widths if page_size says A4)
  const declaredPageSize = String(
    (bill as any).page_size ||
    (bill as any).pageSize ||
    (bill as any).template?.layout?.pageSize ||
    ''
  ).toUpperCase();

  const maxElementRight = (elements as any[]).reduce(
    (max: number, el: any) => Math.max(max, (Number(el.x) || 0) + (Number(el.width) || 0)),
    0
  );
  const tableW = Number(tableEl?.width || 0);

  // If table or elements are narrower than 360px, this is ALWAYS a thermal receipt
  const isThermalLayout = (tableW > 0 && tableW <= 360) || (maxElementRight > 0 && maxElementRight <= 360);

  const isThermal =
    declaredPageSize.includes('THERMAL') ||
    declaredPageSize.includes('58MM') ||
    declaredPageSize.includes('80MM') ||
    isThermalLayout;

  const rawPageSize = isThermal
    ? (declaredPageSize.includes('58MM') || declaredPageSize.includes('50MM') || (maxElementRight > 0 && maxElementRight <= 250) ? 'THERMAL_58MM' : 'THERMAL_80MM')
    : (declaredPageSize || 'A4');

  const resolvedPageWidth =
    rawPageSize.includes('58MM') || rawPageSize.includes('50MM') || rawPageSize.includes('48MM')
      ? 220
      : isThermal
      ? 302
      : rawPageSize === 'A5'
      ? 559
      : 794;

  // ⚡ 1. Extract the order token from bill properties or text elements
  let detectedToken =
    (bill as any).order_token ||
    (bill as any).orderToken ||
    (bill as any).token ||
    '';

  if (!detectedToken) {
    const tEl = elements.find((el: any) =>
      el.id?.startsWith('token-') ||
      el._auto?.includes('tracking') ||
      /(?:Order\s+)?Token:\s*([A-Z0-9]+)/i.test(el.textContent || '')
    );
    const m = (tEl?.textContent || '').match(/(?:Order\s+)?Token:\s*([A-Z0-9]+)/i);
    if (m) detectedToken = m[1];
  }

  // ⚡ 2. Tracking QR is active if token exists or bill is a receipt
  const hasTrackingQR =
    Boolean(detectedToken) ||
    (bill as any).document_type === 'receipt' ||
    (bill as any).show_tracking_qr === true ||
    (bill as any).showTrackingQR === true ||
    elements.some((el: any) =>
      el.id?.startsWith('token-') ||
      el._auto?.includes('tracking') ||
      el.id?.includes('tracking')
    );

  // ⚡ 3. Strip pre-baked auto QR elements, widen the badge box, and preserve text translations
  const rollW = isThermal ? resolvedPageWidth : 794;
  const badgeBoxW = 180;
  const badgeBoxX = Math.round((rollW - badgeBoxW) / 2);

  const cleanElements = elements
    .filter(
      (el: any) =>
        el._auto !== 'invoice' &&
        el._auto !== 'invoice_label' &&
        el._auto !== 'tracking' &&
        el._auto !== 'tracking_label' &&
        !el.id?.startsWith('token-qr-') &&
        !el.id?.startsWith('token-label-') &&
        !el.id?.startsWith('preview-invoice-qr') &&
        !el.id?.startsWith('preview-tracking-qr') &&
        !(el.textContent && el.textContent.includes('Your Order Token:'))
    )
    .map((el: any) => {
      const yVal = Number(el.y || 0);
      const hVal = Number(el.height || 0);

      if (isThermal) {
        // 1. Top dark banner: bleed edge-to-edge across the whole 302px receipt
        if (yVal <= 45 && (el.type === 'shape' || el.type === 'rect' || el.type === 'box') && hVal <= 35) {
          return { ...el, x: 0, width: rollW };
        }

        // 2. Universal "Infinity" Dividers: 100% edge-to-edge across the receipt
        const isDividerLine =
          el.type === 'line' ||
          el.shapeType === 'line' ||
          (el.type !== 'text' && el.type !== 'table' && (hVal <= 4 || (hVal <= 8 && Number(el.width || 0) > 40)));

        if (isDividerLine && yVal > 20) {
          return { ...el, x: 0, width: rollW };
        }

        // 3. Badge rectangle box: centered at badgeBoxX with 180px width
        if ((el.type === 'shape' || el.type === 'rect' || el.type === 'rectangle' || el.type === 'box') && yVal > 40 && yVal < 180 && hVal <= 50) {
          return { ...el, x: badgeBoxX, width: badgeBoxW };
        }

        // 4. Badge text: checks both el.text and el.textContent, locked to exact same x and width as the box
        const textVal = String(el.textContent || el.text || el.content || '');
        if ((el.type === 'text' || el.type === 'textbox') && /parking\s*ticket/i.test(textVal)) {
          const ar =
            el.arabicContent ||
            el.arabic_content ||
            el.translatedContent ||
            el.translated_content ||
            dynAr[el.id] ||
            '';

          return {
            ...el,
            x: badgeBoxX,
            width: badgeBoxW,
            textAlign: 'center',
            textContent: textVal.trim() || 'PARKING TICKET',
            text: textVal.trim() || 'PARKING TICKET',
            arabicContent: ar,
            translatedLang: el.translatedLang || el.translated_lang || translatedLang,
          };
        }
      }

      if (el.type === 'text') {
        const ar =
          el.arabicContent ||
          el.arabic_content ||
          el.translatedContent ||
          el.translated_content ||
          dynAr[el.id] ||
          '';

        return {
          ...el,
          arabicContent: ar,
          translatedLang: el.translatedLang || el.translated_lang || translatedLang,
          bilingualGap: el.bilingualGap ?? el.bilingual_gap ?? 6,
        };
      }
      return el;
    });

  // ⚡ Extract English & Arabic amount in words from bill or dynamic values
  const amountInWords =
    (bill as any).amount_in_words ||
    (bill as any).amountInWords ||
    '';

  const amountInWordsTranslated =
    (bill as any).amount_in_words_ar ||
    (bill as any).amountInWordsAr ||
    (bill as any).amount_in_words_translated ||
    (bill as any).amountInWordsTranslated ||
    dynAr.amount_in_words ||
    dynAr.amount_in_words_ar ||
    '';

  // ⚡ Calculate real table height so Amount in Words never overlaps the totals box!
  const rowCount = Math.max(lineItems.length, 1);
  const taxCount = taxes.filter(t => t.rate > 0).length + (customTaxItem ? 1 : 0);
  const discCount = discounts.filter(d => d.rate > 0).length;
  const rowHeight = isThermal ? 26 : 38;
  const headerHeight = isThermal ? 32 : 44;
  const totalsRowHeight = isThermal ? 22 : 24;
  const totalsBoxHeight = 55 + (taxCount + discCount + 3) * totalsRowHeight;
  const estimatedTableHeight = headerHeight + (rowCount * rowHeight) + totalsBoxHeight;

  return {
    template: { ...bill, elements: cleanElements },
    estimatedTableHeight, // 👈 Pushes tableBottom and Amount in Words below the Subtotal box!
    invoiceDate: bill.createdAt
      ? new Date(bill.createdAt.endsWith('Z') || bill.createdAt.includes('+') ? bill.createdAt : bill.createdAt + 'Z').toLocaleDateString()
      : '',
    invoiceTime: bill.createdAt
      ? new Date(bill.createdAt.endsWith('Z') || bill.createdAt.includes('+') ? bill.createdAt : bill.createdAt + 'Z').toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
      : '',
    lineItems,
    taxLines: taxes,
    discountLines: discounts,
    shippingCost: (tableEl as any)?.shippingCost || 0,
    clientName,
    clientEmail,
    clientPhone,
    clientAddress,
    clientAddressAr,
    clientNameAr,
    buyerTaxId,
    isB2B,
    notes: (bill as any).notes || '',
    currency: bill.currency || 'INR',
    isBilingual: wasTranslated,
    bilingualMode: wasTranslated,
    translateTargetLang: translatedLang,
    showAmountInWords: (bill as any).show_amount_in_words ?? (bill as any).showAmountInWords ?? false,
    amountInWords,
    amountInWordsTranslated,
    amount_in_words: amountInWords,
    amount_in_words_ar: amountInWordsTranslated,
    translatedValues: {
      amountInWords: amountInWordsTranslated,
      amount_in_words: amountInWordsTranslated,
      ...dynAr,
    },
    showInvoiceQR: (bill as any).show_invoice_qr ?? (bill as any).showInvoiceQR ?? true,
    showTrackingQR: hasTrackingQR,
    orderToken: detectedToken,
    token: detectedToken,
    trackingToken: detectedToken,
    invoiceNumber: (bill as any).invoice_number || bill.invoiceNumber || '',
    pageSize: rawPageSize,
    pageWidth: resolvedPageWidth,
    isThermal,
    autoQrSize: isThermal ? 72 : 100,        // ⚡ Compact 72px QR codes for thermal!
    autoQrLabelSize: isThermal ? 8 : 11,     // ⚡ Crisp small font for thermal labels
    autoQrPosition: 'center',
    taxBreakdown: taxBreakdown,
    taxBreakdownAr: savedTaxBreakdownAr,
    dynamicValues: dyn,
    dynamicValuesAr: dynAr,
    dynamicFieldLabelsAr: (bill as any).dynamicFieldLabelsAr || (bill as any).dynamic_field_labels_ar || {},
    sellerCountry: (bill as any).sellerCountry || (bill as any).seller_country,
    sellerRegion: (bill as any).sellerRegion || (bill as any).seller_region,
    buyerRegion: (bill as any).buyerRegion || (bill as any).buyer_region,
    savedElements: cleanElements,
  };
}